/**
 * ListeningPage.js
 *
 * Handles the Listening section of the mock test, including both practice and simulation modes.
 * Optimized for readability, maintainability, and performance.
 * Senior-level comments and structure for future maintainers.
 */

import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { showNotification } from "../../common/headerSlice";
import {
  useLazyGetPracticeTestDataQuery,
  useSubmitTestAnswersMutation,
  useLazyGetTestAttemptResultsQuery,
  useUpdateTestAttemptMutation,
  useLazyGetInstructionsQuery,
  useAcknowledgeInstructionMutation,
} from "../mockTestSlice";
import ListeningSection from "./ListeningSection";
import TestResultsPopup from "./TestResultsPopup";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";

/* --------------------- UTILITY HELPERS --------------------- */
// Cleans heading by collapsing whitespace and formatting for display.
function cleanHeading(h = "") {
  if (h == null) return "";
  return String(h)
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s{2,}/g, " ")
    .replace(/\s+-\s+/g, " - ")
    .replace(/\s{1,}/g, " ")
    .replace(/\s{2,}/g, " ");
}

// Removes "(n)" tokens shown in question text (shown separately elsewhere)
function stripEmbeddedNum(text = "", qid) {
  if (text == null || qid == null) return text || "";
  const rx = new RegExp(`\\(\\s*${qid}\\s*\\)`, "g");
  return text.replace(rx, "").trim();
}

// Helper function to group mcq_multiple questions by questionRange
// IMPORTANT: Preserves original array order from backend - does NOT sort by questionId
// because some questions have questionId: null (non-blank lines in notes_completion)
function groupMcqMultipleQuestions(questions) {
  const grouped = [];
  const mcqMultipleMap = new Map();
  const mcqMultipleFirstIndex = new Map(); // Track original position of first question in each group

  questions.forEach((q, idx) => {
    if (q.questionType === "mcq_multiple" && q.questionRange) {
      if (!mcqMultipleMap.has(q.questionRange)) {
        mcqMultipleMap.set(q.questionRange, []);
        mcqMultipleFirstIndex.set(q.questionRange, idx); // Store original index
      }
      mcqMultipleMap.get(q.questionRange).push(q);
    } else {
      grouped.push({ item: q, originalIndex: idx });
    }
  });

  // Add grouped mcq_multiple questions with their original index
  mcqMultipleMap.forEach((groupQuestions, range) => {
    if (groupQuestions.length > 0) {
      // Create a grouped question object with all question IDs
      grouped.push({
        item: {
          ...groupQuestions[0], // Use first question's data as base
          questionIds: groupQuestions.map((q) => q.questionId || q._id),
          isGrouped: true,
          groupedQuestions: groupQuestions,
        },
        originalIndex: mcqMultipleFirstIndex.get(range),
      });
    }
  });

  // Sort by original index to preserve backend order
  grouped.sort((a, b) => a.originalIndex - b.originalIndex);

  // Return just the items without the originalIndex wrapper
  return grouped.map((g) => g.item);
}

// Converts wide underscores to a single marker for blank width detection
function underscoreTokenize(text = "") {
  if (text == null) return "";
  return text.replace(/_{2,}/g, "___");
}

/* --------------------- MAIN COMPONENT --------------------- */
export default function ListeningPage() {
  // ---- ROUTER, REDUX, AND INSTRUCTION HOOKS ----
  const { testId, bundleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const audioRef = useRef(null);

  // Detect if this is a bundle test from URL params
  const isBundle = !!bundleId;
  
  // Detect test type from URL path
  const getTestTypeFromPath = () => {
    const path = location.pathname.toLowerCase();
    if (path.includes('/mini-mock-test/')) return 'mini_mock';
    if (path.includes('/class-test/')) return 'class_test';
    return 'full_mock';
  };
  
  const testType = getTestTypeFromPath();

  // Helper to get base route for navigation
  const getBaseRoute = () => {
    if (isBundle) {
      return `/app/bundles/${bundleId}/test/${testId}`;
    }
    // Return correct route based on test type
    if (testType === 'mini_mock') {
      return `/mini-mock-test/${testId}`;
    }
    if (testType === 'class_test') {
      return `/class-test/${testId}`;
    }
    return `/mock-test/${testId}`;
  };

  // Cleanup audio when component unmounts (navigating away from listening)
  useEffect(() => {
    return () => {
      // Pause and reset audio when leaving the listening page
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Instruction modal and dialog controls
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    showTimeExpiryDialog,
    hideTimeExpiryDialog,
    showConfirmationDialog,
    hideConfirmationDialog,
  } = useInstructions();

  // ---- STATE MANAGEMENT ----
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);
  const [instructionsAcknowledged, setInstructionsAcknowledged] =
    useState(false);
  const [listenScreen, setListenScreen] = useState(0);
  const [answers, setAnswers] = useState({});
  const [localListeningData, setLocalListeningData] = useState(null);
  const [currentExtractIndex, setCurrentExtractIndex] = useState(0);
  const [studentAnswerId, setStudentAnswerId] = useState(""); // For API call chaining

  // Notes state - persists across parts/extracts until test completion
  const [notes, setNotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  // Check for essential state and redirect if missing (browser refresh)
  const studentAttemptId = location.state?.studentAttemptId;
  useEffect(() => {
    if (!studentAttemptId || !location.state) {
      console.log("Essential state missing, redirecting to main test screen");
      const redirectRoute = isBundle
        ? `/app/bundles/${bundleId}`
        : getBaseRoute();
      navigate(redirectRoute, { replace: true });
    }
  }, [studentAttemptId, location.state, navigate, testId, isBundle, bundleId]);

  // Static part order for fallback navigation
  const PART_ORDER = ["A", "B", "C"];

  // ---- INITIAL PART LOGIC (Dynamic selection based on navigation state or API data) ----
  const initialPart = (() => {
    // Navigation state-based logic (practice/simulation modes)
    const partsOrder = location.state?.listeningPartsOrder;
    const partIdx = location.state?.currentListeningPartIndex || 0;
    const getApiPartLetter = (partId) => {
      const apiParts =
        location.state?.practiceData?.data?.listeningTest?.listeningParts;
      if (apiParts) {
        const found = apiParts.find((p) => p._id === partId);
        if (found && found.part) return found.part;
      }
      return null;
    };
    // Try practice mode order
    if (
      Array.isArray(partsOrder) &&
      partsOrder.length > 0 &&
      partIdx < partsOrder.length
    ) {
      const letter = getApiPartLetter(partsOrder[partIdx]);
      if (letter) return letter;
    }
    // Try simulation step
    const simStep = location.state?.simulationStep;
    if (
      simStep &&
      Array.isArray(simStep.listeningPartIds) &&
      typeof simStep.listeningPartIndex === "number"
    ) {
      const letter = getApiPartLetter(
        simStep.listeningPartIds[simStep.listeningPartIndex],
      );
      if (letter) return letter;
    }
    // Fallback to first available API part
    const apiParts =
      location.state?.practiceData?.data?.listeningTest?.listeningParts;
    if (apiParts?.length > 0 && apiParts[0].part) return apiParts[0].part;
    // Default to "A"
    return "A";
  })();
  const [currentPart, setCurrentPart] = useState(initialPart);

  // ---- API MUTATION HOOKS ----
  const [triggerGetPracticeData, { data: testData, isLoading, error }] =
    useLazyGetPracticeTestDataQuery();
  const [submitTestAnswers] = useSubmitTestAnswersMutation();
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();
  const [updateTestAttempt] = useUpdateTestAttemptMutation();

  // Instruction API hooks
  const [
    getInstructions,
    { data: instructionData, isLoading: instructionLoading },
  ] = useLazyGetInstructionsQuery();
  const [acknowledgeInstruction] = useAcknowledgeInstructionMutation();

  // Results state for popup
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // ---- TIMER (Practice Mode) ----
  // Timer is now handled entirely in the frontend based on audio duration
  // No longer using API startTime/endTime
  const testAttemptId = location.state?.mockTest?.studentAttemptId;

  // ---- INSTRUCTION MODAL & PART INSTRUCTION HANDLING ----
  const [lastPart, setLastPart] = useState(null);
  const instrOpen =
    instructionState.showInstructions &&
    instructionState.currentSection === "listening";

  // ---- EFFECTS ----

  // Show initial instructions modal at section start (simulation/practice)
  useEffect(() => {
    if (showInitialInstructions && studentAttemptId) {
      // Fetch instructions from API
      getInstructions({
        studentAttemptId,
        module: "listening",
      });
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      showInstructionModal("listening", null, mode);
      setShowInitialInstructions(false);
    }
  }, [
    showInitialInstructions,
    showInstructionModal,
    location.state,
    studentAttemptId,
    getInstructions,
  ]);

  // REMOVED: Part-specific instructions - only show instructions once at the start

  // Timer is now calculated based on audio duration in ListeningSection
  // No need for timer initialization or storage here

  // Main test data fetch logic, handles both practice and simulation modes
  // Wait for instructions to be acknowledged before fetching data
  useEffect(() => {
    const practiceDataFromState = location.state?.practiceData;

    // Allow setting data from navigation state even before acknowledgment
    if (practiceDataFromState) {
      setLocalListeningData(practiceDataFromState);
      return;
    }

    // Don't fetch data from API until instructions are acknowledged
    if (!instructionsAcknowledged) {
      console.log(
        "Waiting for instructions to be acknowledged before fetching listening data",
      );
      return;
    }

    const studentAttemptId = location.state?.studentAttemptId;
    const isPracticeMode = location.state?.isPracticeMode;

    // PRACTICE MODE: fetch current part
    if (studentAttemptId && isPracticeMode) {
      const listeningPartsOrder = location.state?.listeningPartsOrder || [];
      const currentListeningPartIndex =
        location.state?.currentListeningPartIndex || 0;
      const currentPartId = listeningPartsOrder[currentListeningPartIndex];

      // NEW: Use singular listeningPartId instead of array
      if (currentPartId) {
        triggerGetPracticeData({
          studentAttemptId,
          currentSection: "listening",
          listeningId: location.state?.listeningTestId,
          listeningPartId: currentPartId, // CHANGED: singular parameter
        });
      } else {
        // Fallback: if no specific part, use first from array
        const firstPartId =
          location.state?.listeningSectionIds?.[0] ||
          location.state?.listeningPartIds?.[0];
        if (firstPartId) {
          triggerGetPracticeData({
            studentAttemptId,
            currentSection: "listening",
            listeningId: location.state?.listeningTestId,
            listeningPartId: firstPartId, // CHANGED: singular parameter
          });
        }
      }
      return;
    }

    // SIMULATION MODE: fetch by simulationStep
    if (studentAttemptId && location.state?.simulationStep) {
      const simulationStep = location.state.simulationStep;
      // NEW: Use listeningSectionIds instead of listeningPartIds
      const listeningSectionIds =
        simulationStep.listeningSectionIds ||
        simulationStep.listeningPartIds ||
        [];
      const listeningPartIndex = simulationStep.listeningPartIndex || 0;
      const partId = listeningSectionIds[listeningPartIndex];

      if (partId) {
        // NEW: Use singular listeningPartId parameter
        triggerGetPracticeData({
          studentAttemptId,
          currentSection: "listening",
          listeningId: location.state?.listeningTestId,
          listeningPartId: partId, // CHANGED: singular parameter
        });
        return;
      }
    }

    // GENERIC FALLBACK: fetch current part and extract index
    if (studentAttemptId) {
      triggerGetPracticeData({
        studentAttemptId,
        currentPart,
        currentExtract: currentExtractIndex,
        isNextPart: true,
      });
    } else {
      // No studentAttemptId, can't continue
    }
  }, [
    testId,
    location.state?.studentAttemptId,
    location.state?.practiceData,
    location.state?.currentListeningPartIndex,
    location.state?.isPracticeMode,
    location.state?.listeningTestId,
    location.state?.listeningPartIds,
    location.state?.listeningSectionIds,
    location.state?.listeningPartsOrder,
    location.state?.simulationStep,
    currentPart,
    currentExtractIndex,
    instructionsAcknowledged,
    triggerGetPracticeData,
  ]);

  // Sync simulationStep to part state
  useEffect(() => {
    if (
      location.state?.simulationStep &&
      localListeningData?.data?.listeningTest?.listeningParts
    ) {
      // NEW: Use listeningSectionIds instead of listeningPartIds
      const listeningSectionIds =
        location.state.simulationStep.listeningSectionIds ||
        location.state.simulationStep.listeningPartIds ||
        [];
      const listeningPartIndex =
        location.state.simulationStep.listeningPartIndex;

      if (
        Array.isArray(listeningSectionIds) &&
        typeof listeningPartIndex === "number" &&
        listeningSectionIds.length > listeningPartIndex
      ) {
        const apiParts = localListeningData.data.listeningTest.listeningParts;
        const partObj = apiParts.find(
          (p) => p._id === listeningSectionIds[listeningPartIndex],
        );
        const thisPart = partObj?.part || "A";
        setCurrentPart(thisPart);
        setCurrentExtractIndex(0);
        setListenScreen(0);
        setAnswers({});
      }
    }
  }, [location.state?.simulationStep, localListeningData]);

  // On new API data, reset to first part in response (practice mode)
  useEffect(() => {
    if (testData) {
      const isPracticeMode = location.state?.isPracticeMode;
      const listeningPartsOrder = location.state?.listeningPartsOrder || [];
      const currentListeningPartIndex =
        location.state?.currentListeningPartIndex || 0;
      const currentPartId = listeningPartsOrder[currentListeningPartIndex];

      // Filter test data to show only selected sections in practice mode
      let filteredData = testData;
      if (isPracticeMode && listeningPartsOrder.length > 0 && currentPartId) {
        // Filter listeningParts to only include the current selected part
        if (testData?.data?.listeningTest?.listeningParts) {
          const filteredParts =
            testData.data.listeningTest.listeningParts.filter(
              (p) => p._id === currentPartId,
            );
          filteredData = {
            ...testData,
            data: {
              ...testData.data,
              listeningTest: {
                ...testData.data.listeningTest,
                listeningParts: filteredParts,
              },
            },
          };
        }

        // Filter listeningSections to only include the current selected section
        if (testData?.data?.listeningTest?.listeningSections) {
          const filteredSections =
            testData.data.listeningTest.listeningSections.filter(
              (s) => s._id === currentPartId,
            );
          filteredData = {
            ...testData,
            data: {
              ...testData.data,
              listeningTest: {
                ...testData.data.listeningTest,
                listeningSections: filteredSections,
              },
            },
          };
        }
      }

      setLocalListeningData(filteredData);

      if (isPracticeMode && filteredData?.data?.listeningTest?.listeningParts) {
        const availableParts = filteredData.data.listeningTest.listeningParts;
        if (availableParts.length > 0) {
          setCurrentPart(availableParts[0].part);
          setCurrentExtractIndex(0);
          setListenScreen(0);
          setAnswers({});
        }
      }
    }
  }, [
    testData,
    location.state?.isPracticeMode,
    location.state?.listeningPartsOrder,
    location.state?.currentListeningPartIndex,
  ]);

  // ---- AUTO-NEXT/CONTINUE HANDLERS ----
  // Handles auto-next button logic, proceeds directly like reading section
  const handleAutoNext = async () => {
    await proceedAutoNext();
  };

  // Proceeds to next part/section, submitting answers as needed
  const proceedAutoNext = async () => {
    const studentAttemptId = location.state?.studentAttemptId;
    try {
      // --- SUBMIT ANSWERS (EVEN IF EMPTY) ---
      let listeningTestId = testId;
      if (localListeningData?.data?.listeningTest?._id) {
        listeningTestId = localListeningData.data.listeningTest._id;
      }
      // Helper to send MCQ (optionKey) if needed
      const getMcqOptionKey = (questionId, givenAnswer) => {
        // Support single string answers or arrays for multiple-choice multiple answers
        const resolveSingle = (ans) => {
          // ans is a scalar value - try to find matching option key
          // First search in transformed data structure
          const currentListeningData =
            transformListeningData(localListeningData);
          const searchIn = [];
          if (currentListeningData?.partB?.extracts)
            searchIn.push(...currentListeningData.partB.extracts);
          if (currentListeningData?.partC?.extracts)
            searchIn.push(...currentListeningData.partC.extracts);

          for (const extract of searchIn) {
            if (extract.questions) {
              for (const qWrap of extract.questions) {
                const q = qWrap.content;
                if (
                  String(q.questionId) === String(questionId) &&
                  Array.isArray(q.options)
                ) {
                  const found = q.options.find((opt, idx) => {
                    const optKey = opt?.key ?? String.fromCharCode(65 + idx);
                    return (
                      opt.value === ans ||
                      opt.label === ans ||
                      opt.key === ans ||
                      optKey === ans ||
                      opt.text === ans
                    );
                  });
                  if (found) return found.key || (found.value ?? ans);
                }
              }
            }
          }

          // Fallback to searching API listeningParts structure
          const apiParts =
            localListeningData?.data?.listeningTest?.listeningParts || [];
          for (const part of apiParts) {
            if (part.part === "C" && part.listeningExtract) {
              for (const extract of part.listeningExtract) {
                if (extract.extractQuestion) {
                  for (const question of extract.extractQuestion) {
                    if (
                      String(question.questionId) === String(questionId) &&
                      Array.isArray(question.options)
                    ) {
                      const found = question.options.find((opt, idx) => {
                        const optKey =
                          opt?.key ?? String.fromCharCode(65 + idx);
                        return (
                          opt.value === ans ||
                          opt.label === ans ||
                          opt.key === ans ||
                          optKey === ans ||
                          opt.text === ans
                        );
                      });
                      if (found) return found.key || (found.value ?? ans);
                    }
                  }
                }
              }
            }
          }

          // No mapping found - return original
          return ans;
        };

        if (Array.isArray(givenAnswer)) {
          return givenAnswer.map((g) => resolveSingle(g));
        }
        return resolveSingle(givenAnswer);
      };
      // Get all question IDs from the test
      const allQuestionIds = getAllQuestionIdsFromTest();

      // Build answers array with all questions
      const answersArray = allQuestionIds.map((questionId) => {
        const givenAnswer = answers?.[questionId];
        const processedAnswer = givenAnswer
          ? getMcqOptionKey(questionId, givenAnswer)
          : "";
        console.log(
          `[DEBUG] Processing answer for questionId ${questionId}: original="${givenAnswer}" -> processed="${processedAnswer}"`,
        );
        return {
          questionId: parseInt(questionId) || questionId,
          givenAnswer: processedAnswer || "",
        };
      });

      const submissionData = {
        testId: listeningTestId,
        testAttemptId: studentAttemptId,
        answers: answersArray,
        ...(studentAnswerId &&
          typeof studentAnswerId === "string" &&
          studentAnswerId.trim() !== "" && { studentAnswerId }),
        isNextPart: true,
      };
      console.log(`[DEBUG] Final submission data:`, submissionData);
      const response = await submitTestAnswers(submissionData).unwrap();
      const newStudentAnswerId =
        response?.studentAnswerId ||
        response?.data?.studentAnswerId ||
        response?.data?.data?.studentAnswerId;
      if (newStudentAnswerId) setStudentAnswerId(newStudentAnswerId);

      // --- NAVIGATION LOGIC (practice/simulation/other) ---
      const isPracticeMode = location.state?.isPracticeMode;
      const simulationStep = location.state?.simulationStep;

      if (isPracticeMode) {
        // PRACTICE MODE: move to next part or next section
        const listeningPartsOrder = location.state?.listeningPartsOrder || [];
        const currentListeningPartIndex =
          location.state?.currentListeningPartIndex || 0;
        const nextListeningPartIndex = currentListeningPartIndex + 1;
        if (nextListeningPartIndex < listeningPartsOrder.length) {
          try {
            // NEW: Use singular listeningPartId parameter
            const nextPartPracticeData = await triggerGetPracticeData({
              studentAttemptId,
              currentSection: "listening",
              listeningId: location.state?.listeningTestId,
              listeningPartId: listeningPartsOrder[nextListeningPartIndex], // CHANGED: singular
            }).unwrap();
            navigate(`${getBaseRoute()}/listening`, {
              state: {
                ...location.state,
                currentListeningPartIndex: nextListeningPartIndex,
                practiceData: nextPartPracticeData,
                studentAnswerId: studentAnswerId,
              },
            });
            return;
          } catch {
            // Fallback: ignore fetch error for next part
          }
        }
        const selectedSections = location.state?.selectedSections;
        if (isFinalSection()) {
          await showPracticeResults(studentAttemptId);
        } else if (selectedSections?.reading === true) {
          navigate(`${getBaseRoute()}/reading`, {
            state: { ...location.state, studentAnswerId, practiceData: null },
          });
        } else if (selectedSections?.writing === true) {
          navigate(`${getBaseRoute()}/writing`, {
            state: { ...location.state, studentAnswerId, practiceData: null },
          });
        } else {
          try {
            const resultsResponse =
              await getTestAttemptResults(studentAttemptId).unwrap();
            setTestResults(resultsResponse);
            setShowResultsPopup(true);
          } catch {
            navigate(`/mock-test/${testId}/results`, {
              state: { ...location.state, studentAnswerId },
            });
          }
        }
      } else if (simulationStep) {
        // SIMULATION MODE: move to next part or reading
        // NEW: Use listeningSectionIds instead of listeningPartIds
        const listeningSectionIds =
          simulationStep.listeningSectionIds ||
          simulationStep.listeningPartIds ||
          [];
        const listeningPartIndex = simulationStep.listeningPartIndex || 0;
        const readingSectionIds =
          simulationStep.readingSectionIds ||
          simulationStep.readingPartIds ||
          [];
        const totalReadingParts = simulationStep.totalReadingParts;

        const nextListeningPartIndex = listeningPartIndex + 1;
        if (
          nextListeningPartIndex <
          (simulationStep.totalListeningParts || listeningSectionIds.length)
        ) {
          setLocalListeningData(null);
          navigate(`${getBaseRoute()}/listening`, {
            state: {
              ...location.state,
              simulationStep: {
                ...simulationStep,
                listeningPartIndex: nextListeningPartIndex,
                currentListeningPartId:
                  listeningSectionIds[nextListeningPartIndex], // NEW: add current part ID
              },
              studentAnswerId: studentAnswerId,
              practiceData: null,
            },
          });
        } else {
          // Move to reading section
          navigate(`${getBaseRoute()}/reading`, {
            state: {
              ...location.state,
              simulationStep: {
                ...simulationStep,
                module: "reading", // NEW: update module
                readingPartIndex: 0,
                totalReadingParts,
                readingSectionIds, // NEW: use readingSectionIds
                currentReadingSectionId: readingSectionIds[0], // NEW: add current section ID
              },
              studentAnswerId: studentAnswerId,
              practiceData: null,
            },
          });
        }
      } else {
        // FALLBACK: move to next part in PART_ORDER or to reading
        const currentIdx = PART_ORDER.indexOf(currentPart);
        if (currentIdx !== -1 && currentIdx < PART_ORDER.length - 1) {
          setLocalListeningData(null);
          setCurrentPart(PART_ORDER[currentIdx + 1]);
          setCurrentExtractIndex(0);
          setListenScreen(0);
          setAnswers({});
        } else {
          navigate(`${getBaseRoute()}/reading`, {
            state: { ...location.state, studentAnswerId },
          });
        }
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: "Error submitting answers. Please try again.",
          status: 0,
        }),
      );
    }
  };

  // Handler for answer change in the UI
  const handleAnswerChange = (questionId, value) => {
    console.log(
      `[DEBUG] handleAnswerChange called - questionId: ${questionId}, value: "${value}"`,
    );
    setAnswers((prev) => {
      const newAnswers = {
        ...prev,
        [questionId]: value,
      };
      console.log(`[DEBUG] Updated answers state:`, newAnswers);
      return newAnswers;
    });
  };

  // Handler for adding notes - persists across parts/extracts
  const handleAddNote = (noteData) => {
    const newNote = {
      selectedText: noteData.selectedText,
      userNote: noteData.userNote,
      timestamp: new Date().toLocaleTimeString(),
      id: Date.now(),
      part: currentPart, // Track which part the note was created in
      extractName:
        listeningData?.playlist?.[listenScreen]?.extractName ||
        `Part ${currentPart}`,
    };
    setNotes((prev) => [...prev, newNote]);
  };

  // Helper to determine if this is the last section in practice mode
  const isFinalSection = () => {
    const selectedSections = location.state?.selectedSections;
    if (!selectedSections || !selectedSections.listening) return false;
    return (
      selectedSections.reading !== true && selectedSections.writing !== true
    );
  };

  // Helper to show results for practice mode
  const showPracticeResults = async (studentAttemptId) => {
    try {
      const resultsResponse =
        await getTestAttemptResults(studentAttemptId).unwrap();
      setTestResults(resultsResponse);
      setShowResultsPopup(true);
    } catch {
      dispatch(
        showNotification({
          message: "Error fetching results. Please try again.",
          status: 0,
        }),
      );
    }
  };

  // Helper to get ALL question IDs from the entire listening test
  const getAllQuestionIdsFromTest = () => {
    const allQuestionIds = [];

    // Check new API format with listeningSections
    if (localListeningData?.data?.listeningTest?.listeningSections) {
      const sections = localListeningData.data.listeningTest.listeningSections;
      sections.forEach((section) => {
        const questions = section.questions || [];
        questions.forEach((q) => {
          if (q.questionId != null) {
            allQuestionIds.push(q.questionId);
          }
        });
      });
    }
    // Check legacy API format with listeningParts
    else if (localListeningData?.data?.listeningTest?.listeningParts) {
      const parts = localListeningData.data.listeningTest.listeningParts;
      parts.forEach((part) => {
        const extracts = part.listeningExtract || [];
        extracts.forEach((extract) => {
          const questions = extract.extractQuestion || [];
          questions.forEach((q) => {
            if (q.questionId != null) {
              allQuestionIds.push(q.questionId);
            }
          });
        });
      });
    }

    return allQuestionIds;
  };

  // API -> UI adapter for ListeningSection (formats Part A/B/C structure)
  const transformListeningData = (apiData) => {
    console.log("[DEBUG transformListeningData] Input apiData:", apiData);
    if (!apiData?.data?.listeningTest) {
      console.log("[DEBUG transformListeningData] No listeningTest found");
      return null;
    }
    const listeningTest = apiData.data.listeningTest;
    console.log("[DEBUG transformListeningData] listeningTest:", listeningTest);

    // Formats an audio URL for CDN
    const formatAudioUrl = (audioUrl) => {
      if (!audioUrl) return null;
      let url = audioUrl;
      if (url.startsWith("https://api.mmitsol.com/"))
        url = url.replace("https://api.mmitsol.com/", "");
      if (url.startsWith("/")) url = url.substring(1);
      if (!url.startsWith("https://")) url = "https://" + url;
      return url;
    };

    const result = {};

    // Handle new API format with listeningSections
    if (
      listeningTest?.listeningSections &&
      Array.isArray(listeningTest.listeningSections)
    ) {
      console.log(
        "[DEBUG transformListeningData] Found listeningSections:",
        listeningTest.listeningSections.length,
      );
      listeningTest.listeningSections.forEach((section, idx) => {
        console.log(
          `[DEBUG transformListeningData] Processing section ${idx}:`,
          section,
        );
        const questions = section.questions || [];

        // Group questions by their explicit question.part (A/B/C). If question.part is missing,
        // fallback to section.part, and finally default to A for safety.
        const byPart = { A: [], B: [], C: [] };
        questions.forEach((q) => {
          const p = (q.part || section.part || "A").toUpperCase();
          if (p === "B") byPart.B.push(q);
          else if (p === "C") byPart.C.push(q);
          else byPart.A.push(q);
        });

        // Create extracts per part only when that part has questions
        if (byPart.A.length > 0) {
          const mainHeading = cleanHeading(
            byPart.A[0]?.questionMainHeading || section.title || "General",
          );
          const notes = byPart.A.map((q) => ({
            question_id: q._id,
            questionId: q.questionId,
            question: underscoreTokenize(
              stripEmbeddedNum(q.question, q.questionId),
            ),
            heading: q.questionHeading || q.heading || null,
          }));

          if (!result.partA) result.partA = { extracts: [] };
          result.partA.extracts.push({
            audioURL: formatAudioUrl(section.audioUrl),
            instructions: section.instructions,
            extractName: section.title || "Section",
            questions: [
              {
                content: {
                  instructions: section.instructions,
                  heading: section.title,
                  sections: [
                    {
                      heading: mainHeading,
                      notes,
                    },
                  ],
                },
              },
            ],
          });
        }

        if (byPart.B.length > 0) {
          if (!result.partB) result.partB = { extracts: [] };
          result.partB.extracts.push({
            audioURL: formatAudioUrl(section.audioUrl),
            instructions: section.instructions,
            extractName: section.title || "Section",
            questions: byPart.B.map((q) => ({
              content: {
                question_id: q._id,
                questionId: q.questionId,
                question: q.question,
                options: q.options || [],
                questionType: q.questionType,
                questionNumber: q.questionNumber,
                instructions: q.instructions || section.instructions,
              },
            })),
          });
        }

        if (byPart.C.length > 0) {
          if (!result.partC) result.partC = { extracts: [] };
          result.partC.extracts.push({
            audioURL: formatAudioUrl(section.audioUrl),
            instructions: section.instructions,
            extractName: section.title || "Section",
            questions: byPart.C.map((q) => ({
              content: {
                question_id: q._id,
                questionId: q.questionId,
                question: q.question,
                options: q.options || [],
                questionType: q.questionType,
                questionNumber: q.questionNumber,
                instructions: q.instructions || section.instructions,
              },
            })),
          });
        }
      });

      console.log(
        "[DEBUG transformListeningData] Final result from listeningSections:",
        result,
      );
      return result;
    }

    // Handle legacy API format with listeningParts
    console.log(
      "[DEBUG transformListeningData] Checking for legacy listeningParts format",
    );
    const parts = listeningTest?.listeningParts;
    if (!parts) {
      console.log(
        "[DEBUG transformListeningData] No listeningParts found, returning null",
      );
      return null;
    }

    console.log(
      "[DEBUG transformListeningData] Found listeningParts:",
      parts.length,
    );
    const partA = parts.find((part) => part.part === "A");
    const partB = parts.find((part) => part.part === "B");
    const partC = parts.find((part) => part.part === "C");

    // Format Part A: notes/sections structure
    if (partA) {
      result.partA = {
        extracts:
          partA?.listeningExtract?.map((extract) => {
            const questions = extract.extractQuestion || [];
            const groupedSections = {};
            questions.forEach((q) => {
              const heading = cleanHeading(q.heading || "General");
              if (!groupedSections[heading]) groupedSections[heading] = [];
              groupedSections[heading].push({
                question_id: q._id,
                questionId: q.questionId,
                question: underscoreTokenize(
                  stripEmbeddedNum(q.question, q.questionId),
                ),
                heading: q.heading,
              });
            });
            return {
              audioURL: formatAudioUrl(extract.audioUrl),
              instructions: extract.instructions,
              extractName: extract.extractName,
              questions: [
                {
                  content: {
                    instructions: extract.instructions,
                    heading: extract.extractName,
                    sections: Object.entries(groupedSections).map(
                      ([heading, notes]) => ({
                        heading,
                        notes,
                      }),
                    ),
                  },
                },
              ],
            };
          }) || [],
      };
    }

    // Format Part B and C: MCQ/short-answer
    const formatBCPart = (part) =>
      part?.listeningExtract?.map((extract) => ({
        audioURL: formatAudioUrl(extract.audioUrl),
        instructions: extract.instructions,
        extractName: extract.extractName,
        questions:
          extract.extractQuestion?.map((q) => ({
            content: {
              question_id: q._id,
              questionId: q.questionId,
              question: q.question,
              options: q.options || [],
              questionType: q.questionType,
              questionNumber: q.questionNumber,
              instructions: q.instructions || extract.instructions,
            },
          })) || [],
      })) || [];

    if (partB) result.partB = { extracts: formatBCPart(partB) };
    if (partC) result.partC = { extracts: formatBCPart(partC) };
    console.log(
      "[DEBUG transformListeningData] Final result from listeningParts:",
      result,
    );
    return result;
  };

  /* --------------------- UI RENDERING LOGIC --------------------- */

  // Show loader if loading/waiting for data (but not if we're just waiting for instruction acknowledgment)
  if (isLoading || (!localListeningData && instructionsAcknowledged)) {
    return (
      <>
        {/* Instruction Modal - must render even during loading */}
        <InstructionWrapper
          instructionState={instructionState}
          onInstructionNext={async () => {
            // Acknowledge instruction when user clicks Next
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "listening",
                }).unwrap();
                // Set acknowledged state to trigger data fetch
                setInstructionsAcknowledged(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onInstructionClose={async () => {
            // Acknowledge instruction when modal closes
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "listening",
                }).unwrap();
                setInstructionsAcknowledged(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onConfirmationConfirm={() => {
            hideConfirmationDialog();
          }}
          onConfirmationClose={hideConfirmationDialog}
          onTimeExpiryContinue={hideTimeExpiryDialog}
          timeRemaining={null}
          nextSection="reading"
          instructionData={instructionData?.data}
          instructionLoading={instructionLoading}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress size={60} thickness={5} />
          <Typography sx={{ ml: 2 }}>Loading listening data...</Typography>
        </Box>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">
          Error loading listening test: {error.message}
        </Typography>
      </Box>
    );
  }

  // Pass raw API data directly to ListeningSection (buildPlaylist will handle it)
  const listeningData = localListeningData?.data?.listeningTest || null;
  console.log("[DEBUG render] listeningData (raw API):", listeningData);
  console.log("[DEBUG render] isLoading:", isLoading);
  console.log("[DEBUG render] localListeningData:", localListeningData);

  // Show empty data fallback
  if (!listeningData && !isLoading && localListeningData) {
    console.log(
      "[DEBUG render] Showing 'No listening data available' - listeningData is null/falsy",
    );
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography>No listening data available</Typography>
      </Box>
    );
  }

  // Main UI
  return (
    <>
      {/* Instruction Modal/Dialogs */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={async () => {
          // Acknowledge instruction when user clicks Next
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "listening",
              });
              // Set acknowledged state to trigger data fetch
              setInstructionsAcknowledged(true);
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onInstructionClose={async () => {
          // Acknowledge instruction when modal closes
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "listening",
              });
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        nextSection="reading"
        instructionData={instructionData?.data}
        instructionLoading={instructionLoading}
      />
      {/* Listening Section Main UI */}
      <ListeningSection
        key={`listening-${currentPart}-${
          location.state?.currentListeningPartIndex || 0
        }`}
        listeningData={listeningData}
        listenScreen={listenScreen}
        setListenScreen={setListenScreen}
        audioRef={audioRef}
        instrOpen={instrOpen}
        answers={answers}
        onAnswerChange={handleAnswerChange}
        onAutoNext={handleAutoNext}
        timer="no-limit"
        mode={location.state?.isPracticeMode ? "practice" : "simulation"}
        currentPart={currentPart}
        currentExtractIndex={currentExtractIndex}
        testId={testId}
        bundleId={bundleId}
        // Notes props - persistent across parts/extracts
        notes={notes}
        onAddNote={handleAddNote}
        notesModalOpen={notesModalOpen}
        setNotesModalOpen={setNotesModalOpen}
        onBack={() => {
          const sectionOrder = location.state?.sectionOrder || [];
          const currentSectionIndex = location.state?.currentSectionIndex || 0;
          if (currentSectionIndex > 0) {
            const previousSection = sectionOrder[currentSectionIndex - 1];
            navigate(`${getBaseRoute()}/${previousSection}`, {
              state: {
                ...location.state,
                currentSectionIndex: currentSectionIndex - 1,
              },
            });
          }
        }}
        isLastQuestion={
          location.state?.isPracticeMode &&
          location.state?.sectionOrder &&
          location.state?.currentSectionIndex ===
            location.state?.sectionOrder.length - 1 &&
          (() => {
            const listeningPartsOrder =
              location.state?.listeningPartsOrder || [];
            const currentListeningPartIndex =
              location.state?.currentListeningPartIndex || 0;
            return currentListeningPartIndex === listeningPartsOrder.length - 1;
          })()
        }
        isLastModuleInSimulation={
          !location.state?.isPracticeMode &&
          (() => {
            // In simulation mode, check if listening is the last module in the package
            const mockTest = location.state?.mockTest;
            if (!mockTest) return false;

            // Check if there are any modules after listening (reading or writing only, speaking is not in simulation)
            const hasReading = !!(
              mockTest?.ieltsTest?.readingId || mockTest?.oetTest?.readingId
            );
            const hasWriting = !!(
              mockTest?.ieltsTest?.writingId || mockTest?.oetTest?.writingId
            );

            console.log("ListeningPage isLastModuleInSimulation debug:", {
              hasReading,
              hasWriting,
            });

            // If no reading or writing after listening, this is the last module (speaking not included in simulation)
            return !hasReading && !hasWriting;
          })()
        }
        isLastQuestionInSimulation={
          !location.state?.isPracticeMode &&
          (() => {
            // In simulation mode, check if this is the last part in listening
            const simulationStep = location.state?.simulationStep;
            if (!simulationStep) return false;

            const listeningSectionIds =
              simulationStep?.listeningSectionIds || [];
            const currentIndex = simulationStep?.listeningPartIndex ?? 0;
            const isLastPart = currentIndex >= listeningSectionIds.length - 1;

            console.log("ListeningPage isLastQuestionInSimulation debug:", {
              currentIndex,
              listeningSectionIdsLength: listeningSectionIds.length,
              isLastPart,
            });

            return isLastPart;
          })()
        }
        onFinish={async () => {
          const studentAttemptId = location.state?.studentAttemptId;

          // Handle SIMULATION mode finish
          if (!location.state?.isPracticeMode) {
            try {
              // Submit ALL questions with answers or empty strings
              let listeningTestId = testId;
              if (localListeningData?.data?.listeningTest?._id) {
                listeningTestId = localListeningData.data.listeningTest._id;
              }

              // Get all question IDs from the test
              const allQuestionIds = getAllQuestionIdsFromTest();

              // Build answers array with all questions
              const answersArray = allQuestionIds.map((questionId) => {
                const givenAnswer = answers?.[questionId] ?? "";
                return {
                  questionId: parseInt(questionId) || questionId,
                  givenAnswer: givenAnswer,
                };
              });

              const submissionData = {
                testId: listeningTestId,
                testAttemptId: studentAttemptId,
                answers: answersArray,
                ...(studentAnswerId &&
                  typeof studentAnswerId === "string" &&
                  studentAnswerId.trim() !== "" && { studentAnswerId }),
                isNextPart: true,
              };
              await submitTestAnswers(submissionData).unwrap();

              // Mark test attempt as completed
              if (studentAttemptId) {
                try {
                  await updateTestAttempt({
                    studentAttemptId,
                    isCompleted: true,
                  }).unwrap();
                  console.log("Simulation test attempt marked as completed");
                } catch (updateError) {
                  console.error(
                    "Error marking simulation test as completed:",
                    updateError,
                  );
                }
              }

              // Show results popup (same as practice mode)
              await showPracticeResults(studentAttemptId);
            } catch (error) {
              console.error("Error in simulation onFinish:", error);
              // Still try to show results popup
              await showPracticeResults(studentAttemptId);
            }
            return;
          }

          // Handle PRACTICE mode finish
          if (location.state?.isPracticeMode) {
            try {
              // Submit answers (even if empty)
              let listeningTestId = testId;
              if (localListeningData?.data?.listeningTest?._id) {
                listeningTestId = localListeningData.data.listeningTest._id;
              }
              const getMcqOptionKey = (questionId, givenAnswer) => {
                const resolveSingle = (ans) => {
                  const currentListeningData =
                    transformListeningData(localListeningData);
                  const searchIn = [];
                  if (currentListeningData?.partB?.extracts)
                    searchIn.push(...currentListeningData.partB.extracts);
                  if (currentListeningData?.partC?.extracts)
                    searchIn.push(...currentListeningData.partC.extracts);

                  for (const extract of searchIn) {
                    if (extract.questions) {
                      for (const qWrap of extract.questions) {
                        const q = qWrap.content;
                        if (
                          String(q.questionId) === String(questionId) &&
                          Array.isArray(q.options)
                        ) {
                          const found = q.options.find((opt, idx) => {
                            const optKey =
                              opt?.key ?? String.fromCharCode(65 + idx);
                            return (
                              opt.value === ans ||
                              opt.label === ans ||
                              opt.key === ans ||
                              optKey === ans ||
                              opt.text === ans
                            );
                          });
                          if (found) return found.key || (found.value ?? ans);
                        }
                      }
                    }
                  }

                  const apiParts =
                    localListeningData?.data?.listeningTest?.listeningParts ||
                    [];
                  for (const part of apiParts) {
                    if (part.part === "C" && part.listeningExtract) {
                      for (const extract of part.listeningExtract) {
                        if (extract.extractQuestion) {
                          for (const question of extract.extractQuestion) {
                            if (
                              String(question.questionId) ===
                                String(questionId) &&
                              Array.isArray(question.options)
                            ) {
                              const found = question.options.find(
                                (opt, idx) => {
                                  const optKey =
                                    opt?.key ?? String.fromCharCode(65 + idx);
                                  return (
                                    opt.value === ans ||
                                    opt.label === ans ||
                                    opt.key === ans ||
                                    optKey === ans ||
                                    opt.text === ans
                                  );
                                },
                              );
                              if (found)
                                return found.key || (found.value ?? ans);
                            }
                          }
                        }
                      }
                    }
                  }

                  return ans;
                };

                if (Array.isArray(givenAnswer))
                  return givenAnswer.map((g) => resolveSingle(g));
                return resolveSingle(givenAnswer);
              };
              // Get all question IDs from the test
              const allQuestionIds = getAllQuestionIdsFromTest();

              // Build answers array with all questions
              const answersArray = allQuestionIds.map((questionId) => {
                const givenAnswer = answers?.[questionId];
                const processedAnswer = givenAnswer
                  ? getMcqOptionKey(questionId, givenAnswer)
                  : "";
                return {
                  questionId: parseInt(questionId) || questionId,
                  givenAnswer: processedAnswer || "",
                };
              });

              const submissionData = {
                testId: listeningTestId,
                testAttemptId: studentAttemptId,
                answers: answersArray,
                ...(studentAnswerId &&
                  typeof studentAnswerId === "string" &&
                  studentAnswerId.trim() !== "" && { studentAnswerId }),
                isNextPart: true,
              };
              await submitTestAnswers(submissionData).unwrap();
              if (studentAttemptId) {
                try {
                  await updateTestAttempt({
                    studentAttemptId,
                    isCompleted: true,
                  });
                } catch {}
              }
              await showPracticeResults(studentAttemptId);
            } catch {
              await showPracticeResults(studentAttemptId);
            }
          }
        }}
      />
      {/* Results Popup for Practice Mode */}
      <TestResultsPopup
        open={showResultsPopup}
        onClose={() => setShowResultsPopup(false)}
        resultsData={testResults}
        onContinue={() =>
          navigate(location.state?.backRoute || "/app/mocktests")
        }
        attemptId={studentAttemptId}
        mockTestId={testId}
        baseRoute={location.state?.baseRoute || "/mock-test"}
        backRoute={location.state?.backRoute || "/app/mocktests"}
      />
    </>
  );
}
